import React, {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {fetchAllTimeLeaderboardAll} from "../lib/api";
import {formatLeaderboardDate} from "../lib/utils";
import {COMPETITION_ROWS, QUESTIONS_CATEGORIES} from "../lib/rankingConfig";
import {getGlobalCache, hasGlobalCache, setGlobalCache} from "../state/leaderboardCache";
import {CATEGORIES, LeaderboardScore, ScoreCategory} from "../types/leaderboard";
import "../styles/ranking.css";

interface GlobalCategoryState {
  scores: LeaderboardScore[];
  loading: boolean;
  error: string | null;
  visibleCount: number;
}

function dedupeByUser(scores: LeaderboardScore[]): LeaderboardScore[] {
  const bestByUser = new Map<string, LeaderboardScore>();

  for (const s of scores) {
    const key = s.user_id || s.display_name;
    const existing = bestByUser.get(key);
    if (!existing || s.score > existing.score) {
      bestByUser.set(key, s);
    }
  }

  const unique = Array.from(bestByUser.values());
  unique.sort((a, b) => b.score - a.score);
  return unique;
}

const buildInitialState = (): Record<ScoreCategory, GlobalCategoryState> =>
  (Object.keys(CATEGORIES) as ScoreCategory[]).reduce((acc, key) => {
    acc[key] = { scores: [], loading: true, error: null, visibleCount: 10 };
    return acc;
  }, {} as Record<ScoreCategory, GlobalCategoryState>);

const GlobalRanking: React.FC = () => {
  const [dataByCategory, setDataByCategory] = useState<Record<ScoreCategory, GlobalCategoryState>>(
    () => getGlobalCache<Record<ScoreCategory, GlobalCategoryState>>() ?? buildInitialState()
  );

  // Fetch all categories in ONE request
  useEffect(() => {
    if (hasGlobalCache()) {
      return;
    }
    (async () => {
      try {
        const res = await fetchAllTimeLeaderboardAll();
        const { scoresByCategory } = res;

        setDataByCategory((prev) => {
          const next = { ...prev };
          (Object.keys(CATEGORIES) as ScoreCategory[]).forEach((category) => {
            const rawScores = scoresByCategory[category] || [];
            const uniqueScores = dedupeByUser(rawScores);
            const trimmed = uniqueScores.slice(0, 50); // keep max 50 unique players
            next[category] = {...prev[category], scores: trimmed, loading: false, error: null};
          });
          setGlobalCache(next);
          return next;
        });
      } catch (err: any) {
        const message = err?.message || "Failed to load global leaderboard. See console for details.";
        setDataByCategory((prev) => {
          const next = { ...prev };
          (Object.keys(CATEGORIES) as ScoreCategory[]).forEach((category) => {
            next[category] = {...prev[category], loading: false, error: message};
          });
          setGlobalCache(next);
          return next;
        });
      }
    })();
  }, []);

  const handleShowMore = (category: ScoreCategory) => {
    setDataByCategory((prev) => {
      const current = prev[category];
      const newCount = Math.min(current.visibleCount + 10, current.scores.length);
      const next = { ...prev, [category]: { ...current, visibleCount: newCount } };
      setGlobalCache(next);
      return next;
    });
  };

  const renderCategoryCard = (ScoreCategory: ScoreCategory, titleOverride?: string) => {
    const state = dataByCategory[ScoreCategory];
    const categoryName = titleOverride || CATEGORIES[ScoreCategory].label;
    return (
      <div className="category-card" key={ScoreCategory}>
        <h3 className="category-card-title"><Link to={`/ranking/${encodeURIComponent(ScoreCategory)}`} className="category-link">{categoryName}</Link></h3>
        {state.loading && <div className="muted-text">Loading...</div>}
        {state.error && <div className="error-text">Error: {state.error}</div>}
        {!state.loading && !state.error && (
          <>
            <div className="table-wrapper">
              <table className="ranking-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Player</th>
                    <th>Score</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {state.scores.length === 0 && (
                    <tr>
                      <td colSpan={4} className="muted-text">
                        No global rankings available for this category yet.
                      </td>
                    </tr>
                  )}
                  {state.scores.slice(0, state.visibleCount).map((p, index) => (
                    <tr key={`${p.user_id}-${p.at}`}>
                      <td>{index + 1}</td>
                      <td><Link to={`/profile/${encodeURIComponent(p.display_name)}`} className="player-link">{p.display_name}</Link></td>
                      <td>{p.score.toLocaleString()}</td>
                      <td>{formatLeaderboardDate(p.at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {state.visibleCount < state.scores.length && (<button className="show-more-button" onClick={() => handleShowMore(ScoreCategory)}>Show more</button>)}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="ranking-page">
      <h1 className="page-title">All-Time Rankings</h1>
      <section className="ranking-section">
        <div className="ranking-section-header"><h2>Timed Challenge</h2></div>
        <div className="competition-grid">
          {COMPETITION_ROWS.map((row) => (<div className="competition-row" key={row.label}><div className="competition-row-columns">{row.categories.map(({key, durationLabel}) => renderCategoryCard(key as ScoreCategory, `${row.label} - ${durationLabel}`))}</div></div>))}
        </div>
      </section>

      <section className="ranking-section">
        <div className="ranking-section-header"><h2>Race to 100 Questions</h2></div>
        <div className="questions-grid">{QUESTIONS_CATEGORIES.map((ScoreCategory) => renderCategoryCard(ScoreCategory))}</div>
      </section>
    </div>
  );
};

export default GlobalRanking;
